/* eslint-disable react/jsx-indent */
/* eslint-disable indent */
import './style.scss';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';

import { createOrUpdatePrequalificationCouncilSchema } from 'features/Council/validation';

import {
  changeRequestStatus,
  getPrequalificationCouncilOfProjectProposal,
  updateResult as updateCouncilResult,
} from 'apis/councils.api';

import {
  downloadFile,
  getAllVersions,
  getReviseLogs,
  requestExplanation,
  updateExplanationRequest,
  generalInfoForCouncil,
} from 'apis/projectProposals.api';
import { getAll as getAllOrganizations } from 'apis/organizations.api';

import ErrorDialog from 'components/ErrorDialog';
// eslint-disable-next-line no-unused-vars
import { FileInput, Select, TextareaInput, DatePicker } from 'components/FormControl';
import Loading from 'components/Loading';
import RadioInput from 'components/FormControl/RadioInput';

import { PROJECT_ROLES, TOAST, TIMEZONE_VN, COUNCIL_STATUS } from 'constant';
import { toast } from 'layout';
import {
  formatFileName,
  getAcadamicLevelShortHand,
  getOrganizationNameById,
  isMemberAllowedReview,
  getFilterMembersAreAllowedReview,
  getNumberMembersPresentAndReviewed,
  getEndingTimeOfDate,
  addDaysToDate,
} from 'utils/func';
import DateTimePicker from 'components/FormControl/DateTimePicker';

const formOptions = { resolver: yupResolver(createOrUpdatePrequalificationCouncilSchema) };

export default function EditResultPrequalificationCouncil() {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isVisibleErrorDialog, setIsVisibleDialogError] = useState(false);
  const [allowEditResult, setAllowEditResult] = useState(true);
  const { projectProposalId, councilId, councilTypeId } = useParams();
  const reportFileRef = useRef(null);
  const commentFilesRef = useRef(null);

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors, dirtyFields, isDirty, isValid },
  } = useForm({ ...formOptions, mode: 'onChange' });

  const isOpenedInCouncilForm = watch('isOpened');

  // explanation request (projectProposalReviseLog): thông tin yêu cầu giải trình
  const {
    data: explanationRequestsData,
    isLoading: isExplanationRequestsLoading,
    isError: isExplanationRequestsError,
    error: explanationRequestsError,
    refetch: refetchExplanationRequests,
  } = useQuery(
    ['projectProposalReviseLogs', projectProposalId, 'ALL_REQUEST_EXPLANATION_PREQUALIFICATION'],
    () =>
      getReviseLogs({
        projectProposalId,
        types: ['REQUEST_EXPLANATION_PREQUALIFICATION'],
      }),
    { enabled: !!projectProposalId }
  );
  const explanationRequest = useMemo(() => {
    if (explanationRequestsData?.data?.length > 0) {
      return explanationRequestsData?.data[0];
    }
    return null;
  }, [explanationRequestsData]);

  // prequalification council (thông tin hội đồng sơ tuyển)
  const {
    data: prequalificationCouncilData,
    isLoading: isPrequalificationCouncilLoading,
    isError: isPrequalificationCouncilError,
    error: prequalificationCouncilError,
    refetch: refetchPrequalificationCouncil,
  } = useQuery(['prequalificationCouncil', projectProposalId], () =>
    getPrequalificationCouncilOfProjectProposal({ projectProposalId })
  );
  const prequalificationCouncil = useMemo(
    () => prequalificationCouncilData?.data || null,
    [prequalificationCouncilData]
  );
  const members = useMemo(
    () => prequalificationCouncil?.councilMembers ?? [],
    [prequalificationCouncil]
  );

  // project proposal version 2
  const { data: allProjectProposalVersionsData } = useQuery(
    ['allProjectProposalVersions', projectProposalId],
    () => getAllVersions(projectProposalId)
  );
  const [prequalificationVersion, latestVersion] = useMemo(() => {
    if (allProjectProposalVersionsData?.data?.length > 0) {
      return [
        allProjectProposalVersionsData?.data?.find((item) => item?.version === 2) ?? null,
        allProjectProposalVersionsData?.data?.reduce((a, b) => (a?.version > b?.version ? a : b)),
      ];
    }
    return [];
  }, [allProjectProposalVersionsData]);

  // General info of project proposal (section A)
  const { data: generalInfoProjectProposalData } = useQuery(
    ['GeneralInfoProjectProposal', prequalificationVersion?.id],
    () => generalInfoForCouncil(latestVersion?.id),
    {
      staleTime: 1000 * 60 * 5,
      enabled: !!latestVersion?.id,
    }
  );
  const generalInfoProjectProposal = useMemo(
    () => generalInfoProjectProposalData?.data,
    [generalInfoProjectProposalData]
  );
  const owner = useMemo(
    () =>
      generalInfoProjectProposal?.projectProposalContacts?.find(
        (item) => item?.projectRole?.code === PROJECT_ROLES.OWNER
      ) ?? {},
    [generalInfoProjectProposal]
  );

  // end General info of project proposal (section A)

  // Organizations (to get organization name for every council member)
  const { data: rawOrganizations } = useQuery(['Organizations'], getAllOrganizations, {
    staleTime: 1000 * 60 * 5,
  });
  const organizations = useMemo(
    () =>
      rawOrganizations?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [rawOrganizations, t]
  );
  // end Organizations

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);
  const {
    mutate: mutateUpdateCouncilResult,
    isLoading: isCreateOrUpdateLoading,
    isError: isCreateOrUpdateError,
    error: createOrUpdateError,
  } = useMutation(updateCouncilResult);
  const {
    // eslint-disable-next-line no-unused-vars
    mutate: mutateChangeRequestStatus,
    isLoading: isChangeRequestStatusLoading,
    isError: isChangeRequestStatusError,
    error: changeRequestStatusError,
  } = useMutation(changeRequestStatus);

  const {
    mutate: mutateUpdateExplanationRequest,
    isLoading: isUpdateExplanationRequestLoading,
    isError: isUpdateExplanationRequestError,
    error: updateExplanationRequestError,
  } = useMutation(updateExplanationRequest);

  const {
    mutate: mutateRequestExplanation,
    isLoading: isRequestExplanationLoading,
    isError: isRequestExplanationError,
    error: requestExplanationError,
  } = useMutation(requestExplanation);

  const councilStatus = watch('councilStatus');
  const oldCommentFileUrls = watch('oldCommentFileUrls');
  // #endregion

  // #region Events
  useEffect(() => {
    const currentValues = watch();
    reset({
      ...currentValues,
      councilStatus:
        prequalificationCouncil?.councilStatus ??
        prequalificationCouncil?.projectProposalCouncils?.[0]?.councilStatus,
      reportFileUrl:
        prequalificationCouncil?.reportFileUrl ??
        prequalificationCouncil?.projectProposalCouncils?.[0]?.councilStatus,
      reportFile: null,
      comment: explanationRequest?.comment,
      oldCommentFileUrls: explanationRequest?.commentFileUrls ?? [],
      commentFiles: null,
      meetingDate: prequalificationCouncil?.projectProposalCouncils?.[0]?.timeMet
        ? new Date(prequalificationCouncil?.projectProposalCouncils?.[0]?.timeMet)
        : '',
      reviewDeadline: prequalificationCouncil?.projectProposalCouncils?.[0]?.reviewDeadline
        ? new Date(prequalificationCouncil?.projectProposalCouncils?.[0]?.reviewDeadline)
        : '',
      deadline: explanationRequest?.deadline ? new Date(explanationRequest?.deadline) : null,
      conclusion: prequalificationCouncil?.projectProposalCouncils?.[0]?.conclusion,
      // Dùng các field này cho Trường hợp chưa yêu cầu giải trình lần nào
      isOpened: explanationRequest?.isOpened ?? false,
      isEditAllowed: explanationRequest?.isEditAllowed ?? false,

      isShowReview: prequalificationCouncil?.isShowReview ?? false,
      isShowReport: prequalificationCouncil?.isShowReport ?? false,
    });
    prequalificationCouncil?.councilMembers?.forEach((member) => {
      setValue(
        `members.${member.id}`,
        member.projectProposalCouncilMembers?.[0]?.isCalculateScore ?? ''
      );
    });
  }, [prequalificationCouncil, explanationRequest]);

  const onSubmit = (data) => {
    const formData = new FormData();

    formData.append('projectProposalId', projectProposalId);
    formData.append('councilTypeId', councilTypeId);
    formData.append('councilId', councilId);
    formData.append('councilStatus', data?.councilStatus);
    formData.append('members', JSON.stringify(data?.members));
    if (members?.length === 0) {
      formData.append('noMemberPreQualificationCouncil', true);
    }

    if (data?.reportFile) {
      formData.append('reportFile', data?.reportFile);
    }

    if (data?.meetingDate) {
      formData.append('timeMet', new Date(data?.meetingDate));
    }

    const explanationDeadline = getEndingTimeOfDate(
      data?.deadline ? new Date(data?.deadline) : addDaysToDate(new Date(), 7)
    );
    formData.append('deadline', explanationDeadline);
    if (data?.conclusion) {
      formData.append('conclusion', data?.conclusion);
    }
    formData.append('isShowReview', data?.isShowReview);
    formData.append('isShowReport', data?.isShowReport);

    mutateUpdateCouncilResult(formData, {
      onSuccess: () => {
        if (
          (dirtyFields.isOpened ||
            dirtyFields.comment ||
            (dirtyFields.commentFiles && data?.commentFiles?.length > 0) ||
            dirtyFields.oldCommentFileUrls ||
            dirtyFields.deadline ||
            (councilStatus === COUNCIL_STATUS.QUALIFIED && !data?.deadline) ||
            dirtyFields.isEditAllowed) &&
          councilStatus !== COUNCIL_STATUS.NOT_REVIEWED
        ) {
          const requestFormData = new FormData();

          requestFormData.append('projectProposalId', projectProposalId);
          if (explanationRequest?.id) {
            requestFormData.append('id', explanationRequest?.id);
          }

          if (data?.commentFiles?.length > 0) {
            data?.commentFiles?.forEach((file) => {
              requestFormData.append('commentFiles', file);
            });
          }
          requestFormData.append('comment', data?.comment ?? '');

          if (oldCommentFileUrls?.length > 0) {
            requestFormData.append('oldCommentFileUrls', JSON.stringify(oldCommentFileUrls ?? []));
          }
          requestFormData.append('deadline', explanationDeadline);

          requestFormData.append('isOpened', data?.isOpened ? 'true' : 'false');
          requestFormData.append('isEditAllowed', data?.isEditAllowed ? 'true' : 'false');
          // if isSendMailAllowed = false => not send email request explanation
          // else: check some more conditions to send email request explanation
          requestFormData.append('isSendMailAllowed', 'true');

          const mutate = explanationRequest?.id
            ? mutateUpdateExplanationRequest
            : mutateRequestExplanation;

          if (!explanationRequest?.id) {
            requestFormData.append('type', 'REQUEST_EXPLANATION_PREQUALIFICATION');
          }

          mutate(requestFormData, {
            onSuccess: () => {
              refetchExplanationRequests();

              toast(TOAST.SUCCESS, t('council.edit.successResult'));
              refetchPrequalificationCouncil();
              setAllowEditResult(false);

              commentFilesRef.current.clear();
            },
            onError: () => {
              setIsVisibleDialogError(true);
            },
          });
        } else {
          toast(TOAST.SUCCESS, t('council.edit.successResult'));
          refetchPrequalificationCouncil();
          setAllowEditResult(false);
        }

        reportFileRef.current.clear();
      },
      onError: () => {
        setIsVisibleDialogError(true);
      },
    });
  };

  // eslint-disable-next-line no-shadow
  const getNumberMembersReviewed = () =>
    getNumberMembersPresentAndReviewed(prequalificationCouncil?.councilMembers);

  const handleDownloadFile = (path) => {
    mutateDownloadFile(
      {
        projectProposalId,
        path,
      },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);
            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  const checkValidation = () => {
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    }
  };

  const footerConfirmationPrompt = (onConfirm, onCancel) => (
    <div>
      <Button
        label={t('formLayout.action.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={onCancel}
        autoFocus
      />
      <Button
        label={t('formLayout.action.ok')}
        className="w-7rem"
        severity="info"
        onClick={onConfirm}
      />
    </div>
  );
  // #endregion

  return (
    <>
      {(isCreateOrUpdateLoading ||
        isPrequalificationCouncilLoading ||
        isChangeRequestStatusLoading ||
        isExplanationRequestsLoading ||
        isUpdateExplanationRequestLoading ||
        isRequestExplanationLoading) && <Loading />}

      <ErrorDialog
        title="Lỗi lấy thông tin giải trình sơ tuyển"
        isError={isExplanationRequestsError}
        error={explanationRequestsError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />
      <ErrorDialog
        title="Lỗi lấy thông tin hội đồng sơ tuyển"
        isError={isPrequalificationCouncilError}
        error={prequalificationCouncilError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />
      <ErrorDialog
        title="Lỗi cập nhật kết quả sơ tuyển"
        isError={isCreateOrUpdateError}
        error={createOrUpdateError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />
      <ErrorDialog
        title="Lỗi chuyển trạng thái yêu cầu giải trình"
        isError={isChangeRequestStatusError}
        error={changeRequestStatusError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />
      <ErrorDialog
        title="Lỗi tạo yêu cầu giải trình"
        isError={isRequestExplanationError}
        error={requestExplanationError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />
      <ErrorDialog
        title="Lỗi cập nhật yêu cầu giải trình"
        isError={isUpdateExplanationRequestError}
        error={updateExplanationRequestError}
        visible={isVisibleErrorDialog}
        setVisible={setIsVisibleDialogError}
      />

      <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields).length}>
        {({ isActive, onConfirm, onCancel }) => (
          <Dialog
            visible={isActive}
            header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
            position="center"
            style={{ width: '300px' }}
            onHide={onCancel}
            draggable={false}
            footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
          >
            <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
          </Dialog>
        )}
      </ReactRouterPrompt>
      <div className="card relative detail-info-project-proposal">
        <h3 className="mb-5 mt-4 line-height-3 text-4xl text-center font-bold">
          {t('council.edit.resultPrequalificationCouncil')}
        </h3>
        <div className="text-lg mb-3">
          <h4> {t('council.view.overviewLabel').toUpperCase()}</h4>

          <div className="ml-5 mb-2 grid">
            <div className="col">
              <span className="font-semibold">
                {t('projectProposal.general.projectNameAndType.vietnameseName')}:{' '}
              </span>
              <span className="w-full">{generalInfoProjectProposal?.vietnameseName}</span>
            </div>
          </div>

          <div className="ml-5 mb-2 grid">
            <div className="col">
              <span className="font-semibold">
                {t('projectProposal.general.projectNameAndType.englishName')}:{' '}
              </span>
              <span className="w-full">{generalInfoProjectProposal?.englishName}</span>
            </div>
          </div>

          <div className="ml-5 mb-2 grid">
            <div className="col">
              <span className="font-semibold">
                {' '}
                {t('projectProposal.general.ownerSection.labelCouncil')}:{' '}
              </span>
              <span className="w-full">
                {`${getAcadamicLevelShortHand(
                  owner?.academicRank,
                  owner?.academicDegree,
                  t,
                  true
                )}${owner?.fullname ?? generalInfoProjectProposal?.owner?.fullname ?? ''}`}
              </span>
            </div>
          </div>

          {/* Council info */}
          <h4>{t('council.view.councilInfoLabel.preliminarySelectionCouncil').toUpperCase()}</h4>

          <div className="ml-5 mb-2 grid">
            <div className="col">
              <span className="font-semibold">
                {t('council.fields.name.specializationCouncil')}:{' '}
              </span>
              <span className="w-full">
                {prequalificationCouncil?.name ??
                  `Sơ tuyển (${generalInfoProjectProposal?.vietnameseName})`}
              </span>
            </div>
          </div>

          <div className="ml-5 grid">
            <div className="col-12 sm:col-6 md:col-6 lg:col-6">
              <span className="font-semibold">{t('council.fields.establishDate')}: </span>
              <span>
                {prequalificationCouncil?.timeCreated &&
                  new Date(prequalificationCouncil?.timeCreated).toLocaleDateString('en-GB', {
                    timeZone: TIMEZONE_VN,
                  })}
              </span>
            </div>

            <div className="col-12 sm:col-6 md:col-6 lg:col-6">
              <span className="font-semibold">{t('council.fields.decisionToEstablish')}: </span>
              {prequalificationCouncil?.projectProposalCouncils?.[0]?.establishmentFileUrl && (
                <span
                  className="p-link text-primary underline"
                  onClick={() =>
                    handleDownloadFile(
                      prequalificationCouncil?.projectProposalCouncils?.[0]?.establishmentFileUrl,
                      prequalificationCouncil?.id
                    )
                  }
                >
                  {t('council.view.decision')}
                </span>
              )}
            </div>

            <div className="col-12 sm:col-6 md:col-6 lg:col-6">
              <span className="font-semibold">{t('council.fields.reviewDeadline')}: </span>
              {prequalificationCouncil?.projectProposalCouncils?.[0]?.reviewDeadline &&
                new Date(
                  prequalificationCouncil?.projectProposalCouncils?.[0]?.reviewDeadline
                ).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN })}
            </div>

            <div className="col-12 sm:col-6 md:col-6 lg:col-6">
              <span className="font-semibold">{t('council.fields.meetingDate')}: </span>
              <span className="w-full">
                {prequalificationCouncil?.projectProposalCouncils?.[0]?.reviewDeadline &&
                  prequalificationCouncil?.projectProposalCouncils?.[0]?.timeMet &&
                  new Date(
                    prequalificationCouncil?.projectProposalCouncils?.[0]?.timeMet
                  ).toLocaleDateString('en-GB', { timeZone: TIMEZONE_VN })}
              </span>
            </div>

            <div className="col-12 sm:col-6 md:col-6 lg:col-6">
              <span className="font-semibold">
                {' '}
                {t('council.fields.numberOfMembersReviewed')}:{' '}
              </span>
              <span className="w-full">
                {getNumberMembersReviewed()}/
                {getFilterMembersAreAllowedReview(prequalificationCouncil?.councilMembers)?.length}
              </span>
            </div>

            <div className="col-12 sm:col-6 md:col-6 lg:col-6">
              <span className="font-semibold">{t('council.fields.result')}: </span>
              <span>
                {prequalificationCouncil?.projectProposalCouncils?.[0]?.councilStatus ===
                  COUNCIL_STATUS.NOT_REVIEWED && <span>{t('council.viewList.notReviewed')}</span>}
                {prequalificationCouncil?.projectProposalCouncils?.[0]?.councilStatus ===
                  COUNCIL_STATUS.NOT_QUALIFIED && <span>{t('council.viewList.notQualified')}</span>}
                {prequalificationCouncil?.projectProposalCouncils?.[0]?.councilStatus ===
                  COUNCIL_STATUS.QUALIFIED && <span>{t('council.viewList.qualified')}</span>}
              </span>
            </div>
          </div>

          {members?.length > 0 ? (
            <div className="form-table ml-5 mt-3">
              <div className="mb-2" style={{ fontWeight: 900, fontSize: '17px' }}>
                {t('council.edit.memberList')}
              </div>
              <table>
                <thead>
                  <tr>
                    <th className="text-center" style={{ minWidth: 50, width: 50 }}>
                      {t('formLayout.numericalOrder')}
                    </th>
                    <th className="text-center" style={{ minWidth: 200, width: '30%' }}>
                      {`${t('council.member.fields.academicRank')}, ${t(
                        'council.member.fields.academicDegree'
                      )}, ${t('council.member.fields.name')}`}
                    </th>
                    <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                      Email
                    </th>
                    <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                      {t('council.member.fields.organization')}
                    </th>
                    <th className="text-center" style={{ minWidth: 200, width: '33%' }}>
                      {t('council.member.fields.role')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {members?.map((member, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={member.id}>
                      <td className="text-center">{index + 1}</td>
                      <td>
                        {`${getAcadamicLevelShortHand(
                          member?.contact?.scientificProfile?.academicRank,
                          member?.contact?.scientificProfile?.academicDegree,
                          t,
                          true
                        )}${member?.contact?.fullname}`}
                      </td>
                      <td>{member?.contact?.email}</td>
                      <td>
                        {getOrganizationNameById(
                          member?.contact?.organizationId,
                          organizations,
                          member?.contact?.specificOrganizationName,
                          t
                        )}
                      </td>
                      <Tooltip
                        target=".checkbox-tooltip"
                        position="bottom"
                        content={
                          member?.projectProposalCouncilMembers?.[0]?.isSubmitted === true
                            ? t('council.member.fields.tooltipHasReviewed')
                            : t('council.member.fields.tooltipHasNotReviewed')
                        }
                      />
                      <td>
                        <div className="flex">
                          <div className="w-2">
                            {isMemberAllowedReview(member) && (
                              <Checkbox
                                icon="pi pi-check"
                                disabled
                                checked={member?.projectProposalCouncilMembers?.[0]?.isSubmitted}
                                className="checkbox-tooltip"
                              />
                            )}
                          </div>
                          {member?.councilMemberRole?.code &&
                            t(`council.role.${member?.councilMemberRole?.code}`)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <span className="text-red-500 ml-5 p-2">Hội đồng này không có thành viên nào</span>
          )}
        </div>

        <div className="bg-white p-fluid detail-info-project-proposal">
          <div className="mt-2 ml-5">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="flex">
                <div className="w-3 mr-8">
                  <DateTimePicker
                    label={t('council.fields.meetingDate')}
                    isFontWeightLabel
                    name="meetingDate"
                    control={control}
                    errors={errors}
                  />
                </div>

                <div className="w-3 font-semibold">
                  <Select
                    label={t('council.fields.result')}
                    name="councilStatus"
                    control={control}
                    errors={errors}
                    options={[
                      { name: t('council.viewList.notReviewed'), value: 'notReviewed' },
                      { name: t('council.viewList.notQualified'), value: 'notQualified' },
                      { name: t('council.viewList.qualified'), value: 'qualified' },
                    ]}
                    inputField="value"
                    onSelectChange={(_name, _value) => {
                      if (_value === 'notReviewed') {
                        setValue('comment', explanationRequest?.comment);
                        setValue('commentFiles', null);
                        setValue('oldCommentFileUrls', explanationRequest?.commentFileUrls ?? null);

                        commentFilesRef.current.clear();
                      }
                    }}
                    disabled={
                      !prequalificationCouncil
                        ? false
                        : isPrequalificationCouncilLoading || !allowEditResult
                    }
                    isRequired
                  />
                </div>
              </div>
              <div className="w-full mb-2 font-semibold">
                <FileInput
                  innerRef={reportFileRef}
                  label={t('council.fields.minutes')}
                  name="reportFile"
                  accept=".pdf"
                  acceptOnly="pdfOnly"
                  control={control}
                  errors={errors}
                  disabled={
                    !prequalificationCouncil
                      ? false
                      : isPrequalificationCouncilLoading || !allowEditResult
                  }
                />
                {prequalificationCouncil?.reportFileUrl && (
                  <span
                    className="p-button p-button-text p-button-sm mb-3 font-normal"
                    onClick={() => handleDownloadFile(prequalificationCouncil?.reportFileUrl)}
                  >
                    <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
                    <span className="text-blue-500 underline font-italic text-base">
                      {formatFileName(prequalificationCouncil?.reportFileUrl)}
                    </span>
                  </span>
                )}
              </div>

              {!explanationRequest?.id || allowEditResult ? (
                <div className="w-full mb-2 font-semibold">
                  <TextareaInput
                    label={t('projectProposal.requestExplanation.comment')}
                    name="comment"
                    control={control}
                    errors={errors}
                    row={5}
                    disabled={
                      !prequalificationCouncil && councilStatus !== 'notReviewed'
                        ? false
                        : isChangeRequestStatusLoading ||
                          isExplanationRequestsLoading ||
                          isPrequalificationCouncilLoading ||
                          councilStatus === 'notReviewed' ||
                          !allowEditResult
                    }
                  />
                </div>
              ) : (
                <div className="w-full mb-3">
                  <label htmlFor="commentFiles" className="font-semibold">
                    {t('projectProposal.requestUpdate.comment')}{' '}
                  </label>
                  <p className="ml-2 text-justify" style={{ whiteSpace: 'pre-wrap' }}>
                    {explanationRequest?.comment}
                  </p>
                </div>
              )}

              <div className="w-full mb-2">
                <label htmlFor="commentFiles" className="font-semibold">
                  {t('projectProposal.requestUpdate.commentFile')}{' '}
                </label>
                {oldCommentFileUrls?.map((fileUrl, index) => (
                  <div key={fileUrl?.id ?? index} className="flex justify-content-between m-1 p-1">
                    <span
                      className="text-blue-500 underline font-italic text-base cursor-pointer"
                      onClick={() => handleDownloadFile(fileUrl?.name)}
                    >
                      {formatFileName(fileUrl?.name)}
                    </span>
                    {!isChangeRequestStatusLoading &&
                      !isExplanationRequestsLoading &&
                      !isPrequalificationCouncilLoading &&
                      councilStatus !== 'notReviewed' &&
                      allowEditResult && (
                        <i
                          className="pi pi-times text-red-500 text-xl ml-2"
                          onClick={() => {
                            const fileUrls = [...oldCommentFileUrls];
                            fileUrls.splice(index, 1);
                            setValue('oldCommentFileUrls', fileUrls, { shouldDirty: true });
                          }}
                        />
                      )}
                  </div>
                ))}

                <FileInput
                  innerRef={commentFilesRef}
                  name="commentFiles"
                  control={control}
                  errors={errors}
                  accept=".pdf,.docx,.doc,.xlsx,.xls,.csv"
                  acceptOnly="pdfWordExcelOnly"
                  multiple
                  disabled={
                    !prequalificationCouncil && councilStatus !== 'notReviewed'
                      ? false
                      : isChangeRequestStatusLoading ||
                        isExplanationRequestsLoading ||
                        isPrequalificationCouncilLoading ||
                        councilStatus === 'notReviewed' ||
                        !allowEditResult
                  }
                />
              </div>

              <div className="w-3 mb-2 font-semibold">
                <DatePicker
                  label={t('projectProposal.requestExplanation.deadline')}
                  name="deadline"
                  control={control}
                  errors={errors}
                  disabled={councilStatus === COUNCIL_STATUS.NOT_REVIEWED}
                />
              </div>

              {councilStatus === 'qualified' &&
                (!explanationRequest?.id ||
                  (explanationRequest?.id &&
                    !explanationRequest?.isOpened &&
                    !explanationRequest?.ownerComment)) && (
                  <>
                    <label htmlFor="isOpened" className="block mb-2 font-semibold">
                      {t('projectProposal.requestExplanation.projectProposalRequireExplanation')}
                    </label>
                    <div className="w-full mb-2">
                      <RadioInput
                        name="isOpened"
                        control={control}
                        errors={errors}
                        options={[
                          { value: true, name: t('formLayout.yes') },
                          { value: false, name: t('formLayout.no') },
                        ]}
                        inputField="value"
                        defaultValue={false}
                        disabled={
                          !prequalificationCouncil && councilStatus === 'qualified'
                            ? false
                            : isChangeRequestStatusLoading ||
                              isExplanationRequestsLoading ||
                              isPrequalificationCouncilLoading ||
                              councilStatus !== 'qualified' ||
                              !allowEditResult
                        }
                      />
                    </div>
                  </>
                )}
              {councilStatus === 'qualified' &&
                (!explanationRequest?.id ||
                  (explanationRequest?.id &&
                    !explanationRequest?.isOpened &&
                    !explanationRequest?.ownerComment)) && (
                  // eslint-disable-next-line react/jsx-indent
                  <>
                    <label htmlFor="isEditAllowed" className="block mb-2 font-semibold">
                      {t('projectProposal.requestExplanation.allowOwnerEditProjectProposal')}
                    </label>
                    <div className="w-full mb-2">
                      <RadioInput
                        name="isEditAllowed"
                        control={control}
                        errors={errors}
                        options={[
                          { value: true, name: t('formLayout.yes') },
                          { value: false, name: t('formLayout.no') },
                        ]}
                        inputField="value"
                        defaultValue={false}
                        disabled={councilStatus !== 'qualified' || isOpenedInCouncilForm === false}
                      />
                    </div>
                  </>
                )}

              <label htmlFor="isShowReview" className="block mb-2 font-semibold">
                {t('council.fields.isShowReview')}
              </label>
              <div className="w-full mb-2">
                <RadioInput
                  name="isShowReview"
                  control={control}
                  errors={errors}
                  options={[
                    { value: true, name: t('formLayout.yes') },
                    { value: false, name: t('formLayout.no') },
                  ]}
                  inputField="value"
                  defaultValue={false}
                />
              </div>

              <label htmlFor="isShowReport" className="block mb-2 font-semibold">
                {t('council.fields.isShowReport')}
              </label>
              <div className="w-full mb-2">
                <RadioInput
                  name="isShowReport"
                  control={control}
                  errors={errors}
                  options={[
                    { value: true, name: t('formLayout.yes') },
                    { value: false, name: t('formLayout.no') },
                  ]}
                  inputField="value"
                  defaultValue={false}
                />
              </div>

              <div className="flex flex-wrap justify-content-end mt-2">
                <Button
                  label={t('formLayout.action.close')}
                  severity="danger"
                  type="button"
                  onClick={() => navigate(-1)}
                  className="mr-2 w-8rem"
                />
                <Button
                  label={t('formLayout.action.update')}
                  severity="info"
                  disabled={!(isDirty && Object.keys(dirtyFields).length)}
                  onClick={checkValidation}
                  className="w-8rem"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
